/**
 * Sending a tenant's first-administrator invitation again.
 *
 * @module components/platform/ReissueInvitationForm
 */

import React, { useEffect, useState } from "react";

import type { ReissueDraft } from "@/components/platform/useTenantDirectory";
import { Button, ErrorAlert, FormField, Panel, TextField } from "@/components/ui";
import type { TenantOnboardingSummary } from "@/platform";

export interface ReissueInvitationFormProps {
  tenant: TenantOnboardingSummary;
  pending: boolean;
  error: string | null;
  /** Resolves true when the invitation went, and the form then closes. */
  onSubmit: (tenantId: string, draft: ReissueDraft) => Promise<boolean>;
  onCancel: () => void;
}

/**
 * @example
 * ```tsx
 * <ReissueInvitationForm tenant={tenant} pending={reissuing} error={reissueError}
 *   onSubmit={reissue} onCancel={close} />
 * ```
 */
export function ReissueInvitationForm({
  tenant,
  pending,
  error,
  onSubmit,
  onCancel,
}: ReissueInvitationFormProps): React.ReactElement {
  const [draft, setDraft] = useState<ReissueDraft>({
    email: tenant.nominatedAdminEmail ?? "",
    alternateEmails: "",
    displayName: "",
  });

  const emailId = `reissue-email-${tenant.id}`;
  // The button that opens this form is what it replaces, so focus would
  // otherwise fall to the body and the reader would tab the whole document
  // back to the form they just asked for.
  useEffect(() => {
    document.getElementById(emailId)?.focus();
  }, [emailId]);

  return (
    <Panel tone="inset" className="mt-4">
      <div className="grid gap-3">
        <FormField label="Administrator email" htmlFor={emailId} required>
          <TextField
            id={emailId}
            type="email"
            required
            value={draft.email}
            onChange={(email) => {
              setDraft((current) => ({ ...current, email }));
            }}
            placeholder="oak@pallet-research.edu"
            data-testid={`platform-console-reissue-email-${tenant.id}`}
          />
        </FormField>
        <FormField
          label="Alternate emails"
          htmlFor={`reissue-alternate-emails-${tenant.id}`}
          helpText="Optional, comma-separated."
        >
          <TextField
            id={`reissue-alternate-emails-${tenant.id}`}
            value={draft.alternateEmails}
            onChange={(alternateEmails) => {
              setDraft((current) => ({ ...current, alternateEmails }));
            }}
            placeholder="admin@pallet-research.edu"
            data-testid={`platform-console-reissue-alternate-emails-${tenant.id}`}
          />
        </FormField>
        {error ? (
          <ErrorAlert data-testid={`platform-console-reissue-error-${tenant.id}`}>
            {error}
          </ErrorAlert>
        ) : null}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="primary"
            icon="paper-plane-tilt"
            loading={pending}
            aria-disabled={pending || undefined}
            onClick={() => {
              if (pending) {
                return;
              }
              onSubmit(tenant.id, draft).then((sent) => {
                if (sent) {
                  onCancel();
                }
              });
            }}
            data-testid={`platform-console-reissue-submit-${tenant.id}`}
          >
            {pending ? "Reissuing…" : "Send invitation"}
          </Button>
          <Button size="sm" variant="ghost" aria-disabled={pending || undefined} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </Panel>
  );
}
